import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Card,
  CardContent,
  Modal,
  Stack
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  ZoomIn,
  ZoomOut,
  Today,
  FilterList,
  DateRange,
  NavigateBefore,
  NavigateNext,
  Info,
  CheckCircle,
  Warning,
  Error,
  Schedule,
  Person,
  LocationOn,
  Assignment,
  OpenInNew
} from '@mui/icons-material';
import { AuthContext } from './AuthContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { TimelineSkeleton } from './SkeletonLoaders';

const Timeline = () => {
  const navigate = useNavigate();
  const { selectedBuilding } = useContext(AuthContext);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  
  // State
  const [timelineData, setTimelineData] = useState({ individuals: [], total_forms: 0 });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid'); // 'timeline' or 'grid'
  const [zoomLevel, setZoomLevel] = useState('week'); // day, week, month, year
  const [dateRange, setDateRange] = useState(() => {
    const currentYear = new Date().getFullYear();
    return {
      start: `${currentYear}-01-01`,
      end: `${currentYear}-12-31`
    };
  });
  const [selectedForm, setSelectedForm] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [allIndividuals, setAllIndividuals] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  
  // Fetch timeline data
  const fetchTimelineData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
        // Remove building filter to show everyone
      });
      
      console.log('Fetching timeline data with URL:', `/api/forms/timeline?${params}`);
      const response = await fetchWithAuth(`/api/forms/timeline?${params}`);
      console.log('Timeline response status:', response.status);
      
      if (!response.ok) {
        console.error(`HTTP error! status: ${response.status}`);
        setTimelineData({ individuals: [], total_forms: 0 });
        setLoading(false);
        return;
      }
      
      const data = await response.json();

      // Merge duplicate individuals by individual_id and combine their forms
      const mergedById = new Map();
      for (const item of (data.individuals || [])) {
        const id = item.individual_id;
        if (!mergedById.has(id)) {
          mergedById.set(id, { ...item, forms: [...(item.forms || [])] });
        } else {
          const existing = mergedById.get(id);
          const existingFormsById = new Map((existing.forms || []).map(f => [f.id, f]));
          for (const f of (item.forms || [])) {
            if (!existingFormsById.has(f.id)) existing.forms.push(f);
          }
          // Prefer a non-empty location_name if missing
          if (!existing.location_name && item.location_name) {
            existing.location_name = item.location_name;
          }
          mergedById.set(id, existing);
        }
      }

      const mergedIndividuals = Array.from(mergedById.values());

      // Sort individuals by location name
      const sortedData = {
        ...data,
        individuals: mergedIndividuals.sort((a, b) => {
          const locationA = a.location_name || '';
          const locationB = b.location_name || '';
          return locationA.localeCompare(locationB);
        })
      };

      setTimelineData(sortedData);
    } catch (error) {
      console.error('Error fetching timeline data:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);
  
  // Fetch all individuals for grid view
  const fetchAllIndividuals = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/individuals');
      const individuals = await response.json();
      
      // Also fetch locations to map names
      const locResponse = await fetchWithAuth('/api/locations');
      const locations = await locResponse.json();
      const locationMap = {};
      locations.forEach(loc => {
        locationMap[loc.id] = loc.name;
      });
      
      // Add location names to individuals
      const individualsWithLocations = individuals.map(ind => ({
        ...ind,
        location_name: locationMap[ind.building_location_id] || 'No Location'
      }));
      
      // Sort by location and name
      const sorted = individualsWithLocations.sort((a, b) => {
        const locA = a.location_name || '';
        const locB = b.location_name || '';
        if (locA !== locB) return locA.localeCompare(locB);
        return a.name.localeCompare(b.name);
      });
      
      setAllIndividuals(sorted);
    } catch (error) {
      console.error('Error fetching individuals:', error);
    }
  }, []);

  // Fetch form types
  const fetchFormTypes = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/report-types');
      const data = await response.json();
      setFormTypes(data);
    } catch (error) {
      console.error('Error fetching form types:', error);
    }
  }, []);

  useEffect(() => {
    fetchTimelineData();
    fetchAllIndividuals();
    fetchFormTypes();
  }, [fetchTimelineData, fetchAllIndividuals, fetchFormTypes]);
  
  // Calculate timeline dimensions based on zoom
  const getTimelineScale = () => {
    switch (zoomLevel) {
      case 'day': return 150; // pixels per day
      case 'week': return 30;
      case 'month': return 8;
      case 'year': return 2;
      default: return 8;
    }
  };
  
  // Create compact labels for grid headers
  const getShortLabel = (name) => {
    if (!name) return '';
    const caps = name.match(/\b[A-Z]/g) || [];
    let abbr = caps.join('');
    if (abbr.length < 2) abbr = name.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
    return abbr.slice(0, 4);
  };

  // Get days between two dates
  const daysBetween = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return Math.floor((d2 - d1) / (1000 * 60 * 60 * 24));
  };
  
  // Get position for a date on the timeline
  const getDatePosition = (date) => {
    const scale = getTimelineScale();
    const days = daysBetween(dateRange.start, date);
    return days * scale + 50; // 50px left padding
  };
  
  // Handle zoom
  const handleZoom = (direction) => {
    const levels = ['day', 'week', 'month', 'year'];
    const currentIndex = levels.indexOf(zoomLevel);
    if (direction === 'in' && currentIndex > 0) {
      setZoomLevel(levels[currentIndex - 1]);
    } else if (direction === 'out' && currentIndex < levels.length - 1) {
      setZoomLevel(levels[currentIndex + 1]);
    }
  };
  
  // Handle date range change
  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };
  
  // Center on today
  const centerOnToday = () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(today.getDate() - 30); // 30 days before
    const end = new Date(today);
    end.setDate(today.getDate() + 30); // 30 days after
    
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0]
    });
  };
  
  // Handle form click
  const handleFormClick = (form, individual) => {
    setSelectedForm({
      ...form,
      individual_name: individual.individual_name || individual.name,
      individual_id: individual.individual_id || individual.id
    });
    setModalOpen(true);
  };
  
  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'on_time': return '#4caf50';
      case 'late': return '#ff9800';
      case 'overdue': return '#f44336';
      case 'pending': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };
  
  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'on_time': return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'late': return <Warning sx={{ fontSize: 16 }} />;
      case 'overdue': return <Error sx={{ fontSize: 16 }} />;
      case 'pending': return <Schedule sx={{ fontSize: 16 }} />;
      default: return <Schedule sx={{ fontSize: 16 }} />;
    }
  };
  
  // Handle mouse drag for panning
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - scrollPosition.x, y: e.clientY - scrollPosition.y });
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    setScrollPosition({ x: newX, y: newY });
    if (containerRef.current) {
      containerRef.current.scrollLeft = -newX;
      containerRef.current.scrollTop = -newY;
    }
  };
  
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  
  // Generate date markers
  const generateDateMarkers = () => {
    const markers = [];
    const start = new Date(dateRange.start);
    const end = new Date(dateRange.end);
    const current = new Date(start);
    const today = new Date().toISOString().split('T')[0];

    const minSpacingByZoom = {
      day: 100,
      week: 70,
      month: 60,
      year: 50
    };
    const minSpacing = minSpacingByZoom[zoomLevel] || 60;
    let lastLabelX = -Infinity;
    let lastMonth = -1;

    while (current <= end) {
      const iso = current.toISOString();
      const dateStr = iso.split('T')[0];
      const position = getDatePosition(dateStr);
      const isToday = dateStr === today;
      const isFirstOfMonth = current.getDate() === 1;
      const isNewMonth = current.getMonth() !== lastMonth;

      const label = current.toLocaleDateString('en-US', {
        month: 'short',
        day: zoomLevel === 'year' ? undefined : 'numeric'
      });

      const showLabel = position - lastLabelX >= minSpacing;

      markers.push(
        <Box key={iso} sx={{ position: 'absolute', left: position, top: 0, height: '100%' }}>
          {/* Vertical grid line */}
          <Box sx={{
            position: 'absolute',
            left: 0,
            top: showLabel ? 28 : 0,
            bottom: 0,
            borderLeft: isFirstOfMonth ? '2px solid #90caf9' : '1px solid #e0e0e0'
          }} />

          {/* Date label */}
          {showLabel && (
            <Box sx={{
              position: 'absolute',
              top: 4,
              left: -30,
              width: 60,
              textAlign: 'center'
            }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: isToday ? 'bold' : isFirstOfMonth ? 600 : 400,
                  color: isToday ? '#1976d2' : isFirstOfMonth ? '#1976d2' : '#555',
                  fontSize: isFirstOfMonth ? '0.8rem' : '0.75rem',
                  backgroundColor: isToday ? '#e3f2fd' : 'transparent',
                  px: isToday ? 1 : 0,
                  py: 0.25,
                  borderRadius: 1,
                  whiteSpace: 'nowrap'
                }}
              >
                {label}
              </Typography>
            </Box>
          )}
        </Box>
      );

      if (showLabel) lastLabelX = position;
      lastMonth = current.getMonth();

      switch (zoomLevel) {
        case 'day':
          current.setDate(current.getDate() + 1);
          break;
        case 'week':
          current.setDate(current.getDate() + 7);
          break;
        case 'month':
          current.setMonth(current.getMonth() + 1);
          break;
        case 'year':
          current.setFullYear(current.getFullYear() + 1);
          break;
        default:
          current.setDate(current.getDate() + 7);
      }
    }

    return markers;
  };
  
  // Today line
  const renderTodayLine = () => {
    const today = new Date().toISOString().split('T')[0];
    if (today < dateRange.start || today > dateRange.end) return null;

    const position = getDatePosition(today);
    return (
      <Box
        sx={{
          position: 'absolute',
          left: position,
          top: 0,
          bottom: 0,
          width: 3,
          backgroundColor: '#f44336',
          zIndex: 10,
          opacity: 0.7,
          '&::before': {
            content: '"TODAY"',
            position: 'absolute',
            top: 4,
            left: 6,
            fontSize: 10,
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: '#f44336',
            px: 0.75,
            py: 0.25,
            borderRadius: '4px',
            whiteSpace: 'nowrap',
            letterSpacing: '0.5px'
          }
        }}
      />
    );
  };
  
  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 100px)', width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxSizing: 'border-box' }}>
      {/* Header Controls */}
      <Paper sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimelineIcon color="primary" />
              Forms {viewMode === 'grid' ? 'Grid' : 'Timeline'}
            </Typography>
          </Grid>
          
          <Grid item>
            {/* View Mode Toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, val) => val && setViewMode(val)}
              size="small"
            >
              <ToggleButton value="grid">Grid View</ToggleButton>
              <ToggleButton value="timeline">Timeline View</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          
          <Grid item xs>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
              {/* Date Range */}
              <TextField
                type="date"
                label="Start Date"
                value={dateRange.start}
                onChange={(e) => handleDateChange('start', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="End Date"
                value={dateRange.end}
                onChange={(e) => handleDateChange('end', e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
              
              {/* Zoom Controls - Only show in timeline view */}
              {viewMode === 'timeline' && (
                <>
                  <ToggleButtonGroup
                    value={zoomLevel}
                    exclusive
                    onChange={(e, val) => val && setZoomLevel(val)}
                    size="small"
                  >
                    <ToggleButton value="day">Day</ToggleButton>
                    <ToggleButton value="week">Week</ToggleButton>
                    <ToggleButton value="month">Month</ToggleButton>
                    <ToggleButton value="year">Year</ToggleButton>
                  </ToggleButtonGroup>
                  
                  <IconButton onClick={() => handleZoom('in')} size="small">
                    <ZoomIn />
                  </IconButton>
                  <IconButton onClick={() => handleZoom('out')} size="small">
                    <ZoomOut />
                  </IconButton>
                </>
              )}
              
              <Button
                startIcon={<Today />}
                onClick={centerOnToday}
                variant="outlined"
                size="small"
              >
                Today
              </Button>
            </Stack>
          </Grid>
          
          <Grid item>
            <Chip label={`${timelineData.total_forms} forms`} color="primary" />
          </Grid>
        </Grid>
      </Paper>
      
      {/* Legend */}
      <Paper sx={{ p: 1, mb: 2, flexShrink: 0 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight="bold">Legend:</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
            <Chip
              icon={<CheckCircle />}
              label="On Time"
              size="small"
              sx={{ backgroundColor: '#e8f5e9', color: '#4caf50' }}
            />
            <Chip
              icon={<Schedule />}
              label="Due Soon"
              size="small"
              sx={{ backgroundColor: '#fff3e0', color: '#FFA726' }}
            />
            <Chip
              icon={<Warning />}
              label="Late"
              size="small"
              sx={{ backgroundColor: '#fff8e1', color: '#ff9800' }}
            />
            <Chip
              icon={<Error />}
              label="Overdue"
              size="small"
              sx={{ backgroundColor: '#ffebee', color: '#f44336' }}
            />
            <Chip
              icon={<Schedule />}
              label="Pending"
              size="small"
              sx={{ backgroundColor: '#f5f5f5', color: '#9e9e9e' }}
            />
          </Stack>
        </Stack>
      </Paper>
      
      {/* Grid View */}
      {viewMode === 'grid' ? (
        <Paper sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
          {loading ? (
            <TimelineSkeleton />
          ) : (
            <Box sx={{ minWidth: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ position: 'sticky', top: 0, backgroundColor: '#f5f5f5', zIndex: 10 }}>
                    <th style={{ 
                      padding: '6px 8px', 
                      border: '1px solid #ddd', 
                      textAlign: 'left',
                      verticalAlign: 'top',
                      minWidth: '150px',
                      maxWidth: '180px',
                      backgroundColor: '#e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '13px',
                      height: '70px'
                    }}>
                      Individual / Location
                    </th>
                    {formTypes.map(formType => (
                      <th key={formType.id} style={{ 
                        padding: '3px', 
                        border: '1px solid #ddd',
                        height: '40px',
                        width: '44px',
                        backgroundColor: '#f5f5f5',
                        verticalAlign: 'bottom',
                        textAlign: 'center'
                      }}>
                        <Tooltip title={formType.report_name}>
                          <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.3px' }}>
                            {getShortLabel(formType.report_name)}
                          </span>
                        </Tooltip>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allIndividuals.map((individual, idx) => {
                    // Find forms for this individual in timeline data
                    const individualData = timelineData.individuals.find(i => i.individual_id === individual.id);
                    
                    return (
                      <tr key={individual.id} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa' }}>
                        <td style={{
                          padding: '6px',
                          border: '1px solid #ddd',
                          position: 'sticky',
                          left: 0,
                          backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa',
                          zIndex: 5,
                          minWidth: '150px',
                          maxWidth: '180px'
                        }}>
                          <div
                            style={{ overflow: 'hidden', textOverflow: 'ellipsis', cursor: 'pointer' }}
                            onClick={() => navigate(`/people/${individual.id}`)}
                          >
                            <strong style={{
                              fontSize: '13px',
                              display: 'block',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: '#1976d2',
                              textDecoration: 'none'
                            }}>
                              {individual.name}
                            </strong>
                            <span style={{ fontSize: '11px', color: '#666', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {individual.location_name || 'No Location'}
                            </span>
                          </div>
                        </td>
                        {formTypes.map(formType => {
                          // Find if this individual has this form type
                          const form = individualData?.forms?.find(f => f.report_type_id === formType.id);
                          
                          return (
                            <td key={formType.id} style={{ 
                              padding: '4px', 
                              border: '1px solid #ddd',
                              textAlign: 'center',
                              cursor: form ? 'pointer' : 'default',
                              width: '44px'
                            }}
                            onClick={() => form && handleFormClick(form, individual)}
                            >
                              {form ? (
                                <Tooltip title={
                                  <Box>
                                    <Typography variant="body2">{formType.report_name}</Typography>
                                    <Typography variant="caption">
                                      Doc Date: {new Date(form.due_date).toLocaleDateString()}
                                      {form.collected_date && (
                                        <><br />Due: {new Date(form.collected_date).toLocaleDateString()}</>
                                      )}
                                    </Typography>
                                  </Box>
                                }>
                                  <Box sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '3px',
                                    backgroundColor: getStatusColor(form.timeline_status),
                                    margin: '0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '14px'
                                  }}>
                                    {getStatusIcon(form.timeline_status)}
                                  </Box>
                                </Tooltip>
                              ) : (
                                <Box sx={{
                                  width: 20,
                                  height: 20,
                                  borderRadius: '3px',
                                  backgroundColor: '#f0f0f0',
                                  margin: '0 auto'
                                }} />
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          )}
        </Paper>
      ) : (
      /* Timeline View */
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {loading ? (
          <TimelineSkeleton />
        ) : (
          <>
            {/* Fixed Header Row - Names header + Date markers */}
            <Box sx={{ display: 'flex', flexShrink: 0, borderBottom: '2px solid #bdbdbd', overflow: 'hidden' }}>
              {/* Names column header */}
              <Box sx={{
                width: 200,
                minWidth: 200,
                height: 50,
                display: 'flex',
                alignItems: 'center',
                px: 2,
                backgroundColor: '#1976d2',
                color: 'white',
                borderRight: '2px solid #bdbdbd'
              }}>
                <Person sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold">Individual</Typography>
              </Box>

              {/* Date markers - horizontal scroll synced with content */}
              <Box
                className="date-header-scroll"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: 50,
                  overflowX: 'hidden',
                  overflowY: 'hidden',
                  backgroundColor: '#f5f5f5',
                  position: 'relative'
                }}
              >
                <Box sx={{
                  minWidth: Math.max(600, (daysBetween(dateRange.start, dateRange.end) * getTimelineScale()) + 100),
                  height: '100%',
                  position: 'relative'
                }}>
                  {generateDateMarkers()}
                </Box>
              </Box>
            </Box>

            {/* Content Area - Names + Timeline (single vertical scroll) */}
            <Box
              ref={containerRef}
              sx={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden',
                minWidth: 0,
                maxWidth: '100%'
              }}
            >
              {/* Fixed Names Column */}
              <Box
                className="names-column"
                sx={{
                  width: 200,
                  minWidth: 200,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  borderRight: '2px solid #bdbdbd',
                  backgroundColor: '#fff',
                  '&::-webkit-scrollbar': { display: 'none' },
                  scrollbarWidth: 'none'
                }}
                onScroll={(e) => {
                  const timelineContent = document.querySelector('.timeline-content');
                  if (timelineContent && timelineContent.scrollTop !== e.target.scrollTop) {
                    timelineContent.scrollTop = e.target.scrollTop;
                  }
                }}
              >
                {timelineData.individuals.map((individual, index) => (
                  <Box
                    key={individual.individual_id}
                    sx={{
                      height: 44,
                      display: 'flex',
                      alignItems: 'center',
                      px: 2,
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      '&:hover': { backgroundColor: '#e3f2fd' }
                    }}
                    onClick={() => navigate(`/people/${individual.individual_id}`)}
                  >
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                      <Typography variant="body2" fontWeight="600" noWrap sx={{ fontSize: '0.875rem', color: '#1976d2' }}>
                        {individual.individual_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.7rem' }}>
                        {individual.location_name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Timeline Content - THE ONLY horizontal scrollable area */}
              <Box
                className="timeline-content"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'auto',
                  position: 'relative',
                  '&::-webkit-scrollbar': { height: 12 },
                  '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: '#1976d2', borderRadius: 6 },
                  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#1565c0' }
                }}
                onScroll={(e) => {
                  // Sync vertical scroll with names column
                  const namesColumn = document.querySelector('.names-column');
                  if (namesColumn && namesColumn.scrollTop !== e.target.scrollTop) {
                    namesColumn.scrollTop = e.target.scrollTop;
                  }
                  // Sync horizontal scroll with date header
                  const dateHeader = document.querySelector('.date-header-scroll');
                  if (dateHeader && dateHeader.scrollLeft !== e.target.scrollLeft) {
                    dateHeader.scrollLeft = e.target.scrollLeft;
                  }
                }}
              >
                <Box sx={{
                  minWidth: Math.max(600, (daysBetween(dateRange.start, dateRange.end) * getTimelineScale()) + 100),
                  position: 'relative'
                }}>
                  {/* Today line */}
                  {renderTodayLine()}

                  {/* Individual rows with form markers */}
                  {timelineData.individuals.map((individual, index) => (
                    <Box
                      key={individual.individual_id}
                      sx={{
                        position: 'relative',
                        height: 44,
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: index % 2 === 0 ? '#fff' : '#fafafa',
                        '&:hover': { backgroundColor: '#f5f5f5' }
                      }}
                    >
                      {/* Form markers */}
                      {individual.forms.map((form) => {
                        const position = getDatePosition(
                          form.collected && form.collected_date ? form.collected_date : form.due_date
                        );

                        return (
                          <Tooltip
                            key={form.id}
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography variant="body2" fontWeight="bold">{form.report_name}</Typography>
                                <Typography variant="caption" component="div">
                                  Doc Date: {new Date(form.due_date).toLocaleDateString()}
                                </Typography>
                                {form.collected_date && (
                                  <Typography variant="caption" component="div" sx={{ color: '#4caf50' }}>
                                    Due: {new Date(form.collected_date).toLocaleDateString()}
                                  </Typography>
                                )}
                                {form.collected_by_name && (
                                  <Typography variant="caption" component="div">
                                    By: {form.collected_by_name}
                                  </Typography>
                                )}
                              </Box>
                            }
                            arrow
                          >
                            <Box
                              onClick={() => handleFormClick(form, individual)}
                              sx={{
                                position: 'absolute',
                                left: position - 12,
                                top: 10,
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                backgroundColor: getStatusColor(form.timeline_status),
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: 'white',
                                transition: 'transform 0.15s, box-shadow 0.15s',
                                '&:hover': {
                                  transform: 'scale(1.3)',
                                  boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                                  zIndex: 100
                                }
                              }}
                            >
                              {getStatusIcon(form.timeline_status)}
                            </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>
      )}
      
      {/* Form Details Modal */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
        <Card
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            maxHeight: '80vh',
            overflow: 'auto'
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Form Details
            </Typography>
            {selectedForm && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Individual</Typography>
                  <Typography>{selectedForm.individual_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Form Type</Typography>
                  <Typography>{selectedForm.report_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                  <Chip
                    icon={getStatusIcon(selectedForm.timeline_status)}
                    label={selectedForm.timeline_status?.replace('_', ' ').toUpperCase()}
                    size="small"
                    sx={{ 
                      backgroundColor: getStatusColor(selectedForm.timeline_status) + '20',
                      color: getStatusColor(selectedForm.timeline_status)
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Document Date</Typography>
                  <Typography>{new Date(selectedForm.due_date).toLocaleDateString()}</Typography>
                </Box>
                {selectedForm.collected_date && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Due Date</Typography>
                    <Typography>{new Date(selectedForm.collected_date).toLocaleDateString()}</Typography>
                  </Box>
                )}
                {selectedForm.collected_by_name && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Collected By</Typography>
                    <Typography>{selectedForm.collected_by_name}</Typography>
                  </Box>
                )}
                {selectedForm.days_overdue > 0 && !selectedForm.collected && (
                  <Alert severity="error">
                    {selectedForm.days_overdue} days overdue
                  </Alert>
                )}
              </Stack>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="contained"
                startIcon={<OpenInNew />}
                onClick={() => {
                  setModalOpen(false);
                  navigate(`/people/${selectedForm.individual_id}`);
                }}
              >
                View Profile
              </Button>
              <Button onClick={() => setModalOpen(false)}>Close</Button>
            </Box>
          </CardContent>
        </Card>
      </Modal>
    </Box>
  );
};

export default Timeline;
